import { Button, Card } from 'antd';
import classNames from 'classnames';
import { head, join, last, map, some } from 'lodash-es';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

import {
  SimpleCard,
  SimpleCardTheme,
} from '../../../components/layout/cardElements';
import FlightFormatted from '../../../components/flights/FlightFormatted';
import {
  DateFormatMedium,
  DurationTimeLong,
  TimeFormatWithTimezone,
} from '../../../utils/dateFormatting';
import {
  AirlineLogo,
  UnknownAirlineLogoIcon,
} from '../../../components/flights/AirlineLogos';
import {
  FAIcon,
  FATypes,
} from '../../../components/adapters/fontAwesomeAdapters';
import { CurrencyFormat } from '../../../components/domainSpecific/currencyElements';
import { FlightStops, getFlightDuration, getFlightStops } from './FlightStops';
import { calculateDuration } from '../../../common/utils/dateUtils';
import {
  DeliveryDurationColumn,
  DeliveryTimeColumn,
  DirectDriveIcon,
  DriveMileageColumn,
  DriveTypeColumn,
  TentativeDeliveryColumn,
} from '../../../components/quotes/QuoteRoutingContent';
import { useQuoteRoutingContentStyles } from '../../../components/quotes/QuoteRoutingContent.styles';
import { useRoutingCardsStyles } from './RoutingCards.styles';

const RoutingBadge = ({ labelId }) => {
  const { styles } = useRoutingCardsStyles();
  return (
    <div className={styles.routingCardBadge}>
      <FormattedMessage id={labelId} />
    </div>
  );
};

function SelectButtonColumn({ quoteTotal, onSelectClick }) {
  const { styles } = useRoutingCardsStyles();
  const { styles: contentStyles } = useQuoteRoutingContentStyles();
  return (
    <div className={styles.selectButton}>
      <div className={contentStyles.valueLarge}>
        <CurrencyFormat value={quoteTotal} />
      </div>
      <div>
        <Button type="primary" onClick={onSelectClick} size="small">
          <FormattedMessage id="book.newQuote.routingSelect" />
        </Button>
      </div>
    </div>
  );
}

export const DirectDriveCard = ({ routingOption, onSelected }) => {
  const { styles } = useRoutingCardsStyles();
  const { quoteTotal, origin, destination } = routingOption || {};
  const deliveryDateTime = routingOption?.deliveryDateTime?.scheduled
    ? moment(routingOption.deliveryDateTime.scheduled)
    : null;

  const departureDt = moment(routingOption?.pickupDateTime?.scheduled);
  const driveDuration = calculateDuration(departureDt, deliveryDateTime);

  const onSelectClick = async () => {
    await onSelected(routingOption);
  };

  return (
    <Card
      className={classNames(styles.routingCard, styles.routingCardRecommended)}
    >
      <div className={styles.routingCardContent}>
        <DirectDriveIcon />

        <DeliveryTimeColumn
          label="book.newQuote.driveTime"
          departure={departureDt}
          departureTimezone={origin?.timezone}
          arrival={deliveryDateTime}
          arrivalTimezone={destination?.timezone}
        />

        <DeliveryDurationColumn
          label="book.newQuote.driveDuration"
          duration={driveDuration}
        />

        <DriveMileageColumn distance={routingOption?.origin?.localMileage} />

        <DriveTypeColumn />

        <TentativeDeliveryColumn deliveryDateTime={deliveryDateTime} />

        <SelectButtonColumn
          quoteTotal={quoteTotal}
          onSelectClick={onSelectClick}
        />
      </div>
    </Card>
  );
};

export const StandardRoutingCard = ({
  routingOption,
  onSelected,
  recommended,
  isBestValue,
  isEarliestArrival,
}) => {
  const { styles } = useRoutingCardsStyles();
  const { styles: contentStyles } = useQuoteRoutingContentStyles();
  const { flights, quoteTotal } = routingOption || {};
  const firstFlight = head(flights);
  const lastFlight = last(flights);

  const onSelectClick = async () => {
    await onSelected(routingOption);
  };

  const stopAirports = getFlightStops(flights);
  const deliveryDateTime = routingOption?.deliveryDateTime?.scheduled
    ? moment(routingOption.deliveryDateTime.scheduled)
    : null;

  const firstFlightDeparture = moment(firstFlight?.departure?.estimated);
  const lastFlightArrival = moment(lastFlight?.arrival?.estimated);
  const flightDuration = getFlightDuration(flights);
  return (
    <Card
      className={classNames(styles.routingCard, {
        [styles.routingCardRecommended]: recommended,
      })}
    >
      <div className={styles.routingCardContent}>
        <div
          className={classNames(
            contentStyles.valueLarge,
            contentStyles.valueLargeFixedW
          )}
        >
          <div className="flex-start">
            {isBestValue && (
              <RoutingBadge labelId="book.newQuote.routingsBestValue" />
            )}
            {isEarliestArrival && (
              <RoutingBadge labelId="book.newQuote.earliestArrival" />
            )}
          </div>
          <div className={contentStyles.flightInfo}>
            <AirlineLogo
              airline={firstFlight?.airline}
              fallback={<UnknownAirlineLogoIcon />}
            />
            <div>
              <FlightFormatted flight={firstFlight} />
              <div className={styles.routingCardAirports}>
                {firstFlight?.originAirportCode} -{' '}
                {lastFlight?.destinationAirportCode}
              </div>
            </div>
          </div>
        </div>

        <DeliveryTimeColumn
          label="book.newQuote.flightTime"
          departure={firstFlightDeparture}
          departureTimezone={firstFlight?.originAirportTimezone}
          arrival={lastFlightArrival}
          arrivalTimezone={lastFlight?.destinationAirportTimezone}
        />

        <DeliveryDurationColumn
          label="book.newQuote.flightDuration"
          duration={flightDuration}
        />

        <div>
          <div className={contentStyles.labelSmall}>
            <FormattedMessage id="book.newQuote.connectionType" />
          </div>
          <div className={contentStyles.valueSmall}>
            <FlightStops flights={flights} />
            {some(stopAirports) && <span> | {join(stopAirports)}</span>}
          </div>
        </div>

        <TentativeDeliveryColumn deliveryDateTime={deliveryDateTime} />

        <SelectButtonColumn
          quoteTotal={quoteTotal}
          onSelectClick={onSelectClick}
        />
      </div>
    </Card>
  );
};

export const BestRoutingCard = ({ title, data }) => {
  const { styles } = useRoutingCardsStyles();
  const { flights } = data || {};
  const firstFlight = head(flights);
  const lastFlight = last(flights);
  const firstFlightDeparture = moment(firstFlight?.departure?.estimated);
  const lastFlightArrival = moment(lastFlight?.arrival?.estimated);
  const flightStops = getFlightStops(flights);
  const flightDuration = getFlightDuration(flights);

  return (
    <SimpleCard
      theme={SimpleCardTheme.FLAT}
      title={title}
      className={styles.quoteRoutingTopCard}
    >
      <div className={styles.quoteRoutingTopCardRowTop}>
        <div className="flex-center">
          <div>
            <AirlineLogo
              airline={firstFlight?.airline}
              fallback={<UnknownAirlineLogoIcon />}
            />
          </div>
          <div>
            <FlightFormatted flight={firstFlight} noLabel />
          </div>
        </div>
        <div>
          <p>
            <DurationTimeLong value={flightDuration} /> |{' '}
            <FlightStops flights={flights} />
          </p>
        </div>
      </div>
      <div className={styles.flightsDatesRow}>
        <div className={styles.labelDate}>
          <DateFormatMedium value={firstFlightDeparture} />
        </div>
        <div className={styles.labelDate}>
          <DateFormatMedium value={lastFlightArrival} />
        </div>
      </div>
      <div className={styles.waypointHorizontalWrapper}>
        <div>
          <FAIcon type={FATypes.REGULAR} icon="circle" />
        </div>
        <div className={styles.waypointLineHorizontal} />
        <div className={styles.routingStopsList}>
          {map(flightStops, stop => (
            <div key={stop}>
              <FAIcon icon="plane" />
              <div className={styles.labelTime}>{stop}</div>
            </div>
          ))}
        </div>
        <div>
          <FAIcon type={FATypes.SOLID} icon="dot-circle" />
        </div>
      </div>
      <div className={styles.flightsTimesRow}>
        <div className={styles.labelTime}>
          {firstFlight?.originAirportCode} |{' '}
          <TimeFormatWithTimezone
            value={firstFlightDeparture}
            timeZone={firstFlight?.originAirportTimezone}
          />
        </div>
        <div className={styles.labelTime}>
          {lastFlight?.destinationAirportCode} |{' '}
          <TimeFormatWithTimezone
            value={lastFlightArrival}
            timeZone={lastFlight?.destinationAirportTimezone}
          />
        </div>
      </div>
    </SimpleCard>
  );
};
